import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

const computeAndSetDrawerOffset = () => {
  const announcement =
    document.querySelector('[data-section-type="announcement-bar"]') ||
    document.querySelector('.announcement-bar') ||
    document.querySelector('.header__row[ref="headerRowTop"]');

  const header =
    document.querySelector('.shopify-section-header .header') ||
    document.querySelector('header.header') ||
    document.querySelector('.header');

  const hRect = header ? header.getBoundingClientRect() : null;
  const aRect = announcement ? announcement.getBoundingClientRect() : null;

  const headerHeight = hRect ? hRect.height : 0;

  // Altura visible del announcement dentro del viewport (intersección)
  let announcementVisible = 0;
  if (aRect) {
    const topVisible = Math.max(aRect.top, 0);
    const bottomVisible = Math.min(aRect.bottom, window.innerHeight);
    const intersect = bottomVisible - topVisible; // puede ser fracción (decimales)
    announcementVisible = Math.max(0, Math.min(intersect, aRect.height));
  }

  const offset = headerHeight + announcementVisible; // sin redondeo, decimales exactos
  document.documentElement.style.setProperty('--drawer-top-offset', `${offset}px`);
};

/** @type {number|null} */
let _scrollRaf = null;
/** @returns {void} */
const onScrollRecomputeOffset = () => {
  if (_scrollRaf !== null) return;
  _scrollRaf = window.requestAnimationFrame(() => {
    _scrollRaf = null;
    computeAndSetDrawerOffset();
  });
};

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    computeAndSetDrawerOffset();
    window.addEventListener('resize', computeAndSetDrawerOffset, { passive: true });
    window.addEventListener('orientationchange', computeAndSetDrawerOffset, { passive: true });
    window.addEventListener('scroll', onScrollRecomputeOffset, { passive: true });

    document.addEventListener('shopify:section:load', computeAndSetDrawerOffset);
    document.addEventListener('shopify:section:reorder', computeAndSetDrawerOffset);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('resize', computeAndSetDrawerOffset);
    window.removeEventListener('orientationchange', computeAndSetDrawerOffset);
    window.removeEventListener('scroll', onScrollRecomputeOffset);
    document.removeEventListener('shopify:section:load', computeAndSetDrawerOffset);
    document.removeEventListener('shopify:section:reorder', computeAndSetDrawerOffset);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return /** @type {HTMLDetailsElement} */ (this.refs.details);
    return /** @type {HTMLDetailsElement} */ (event.target.closest('details') ?? this.refs.details);
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    event?.preventDefault();

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) return;

    computeAndSetDrawerOffset();

    if (!details.hasAttribute('open')) {
      details.setAttribute('open', '');
    }

    summary.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    event?.preventDefault(); 
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/** @param {any} x */
/** @returns {x is Element} */
function isElement(x) {
  return x instanceof Element;
}

/** @typedef {Element & { open?: () => void }} CartDrawerEl */

/**
 * @param {(() => void)=} run
 */
function closeMenuThen(run) {
  /** @type {(Element & { isOpen?: boolean; close?: () => void }) | null} */
  const drawer = document.querySelector('header-drawer');

  if (drawer && drawer.isOpen) {
    if (typeof drawer.close === 'function') drawer.close();
    setTimeout(() => {
      if (typeof run === 'function') run();
    }, 180);
  } else {
    if (typeof run === 'function') run();
  }
}

document.addEventListener(
  'click',
  (e) => {
    const tgt = /** @type {any} */ (e.target);
    if (!isElement(tgt)) return;

    const openCartBtn =
      tgt.closest('[data-open-cart], .header__icon--cart, #cart-icon-bubble') ||
      tgt.closest('a[href*="cart"]');

    if (openCartBtn) {
      e.preventDefault();
      closeMenuThen(() => {
        /** @type {CartDrawerEl | null} */
        const cartEl = document.querySelector('cart-drawer');

        if (cartEl && typeof cartEl.open === 'function') {
          cartEl.open();
        } else {
          const fallback = document.querySelector('[data-cart-drawer-open]');
          if (fallback instanceof HTMLElement) {
            fallback.click();
          }
        }
      });
    }
  },
  { passive: false }
);
